import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Box, Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Typography, Divider, Tooltip, Avatar
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PeopleIcon from "@mui/icons-material/People";
import ContactsIcon from "@mui/icons-material/Contacts";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import HistoryIcon from "@mui/icons-material/History";
import BuildIcon from "@mui/icons-material/Build";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { db } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { aggiornaNotifiche } from "../utils/notifiche";
import Notifiche from "./Notifiche";

const DRAWER_WIDTH = 240;

const menuItems = [
  { label: "Lista Eventi", icon: <EventIcon />, path: "/" },
  { label: "Nuovo Evento", icon: <AddCircleOutlineIcon />, path: "/nuovo-evento" },
  { label: "Collaboratori", icon: <PeopleIcon />, path: "/collaboratori" },
  { label: "Referenti", icon: <ContactsIcon />, path: "/referenti" },
  { label: "Statistiche", icon: <BarChartIcon />, path: "/statistiche" },
  { label: "Log operazioni", icon: <HistoryIcon />, path: "/log" },
  { label: "Accessi referenti", icon: <VpnKeyIcon />, path: "/accessi-referenti" },
  { label: "Utility", icon: <BuildIcon />, path: "/utility" },
  { label: "Impostazioni", icon: <SettingsIcon />, path: "/impostazioni" },
];

function LogoVMA({ onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{ cursor: "pointer", px: 1.5, pt: 2, pb: 1.5 }}
    >
      <svg width="100%" viewBox="0 0 200 100" role="img">
        <title>Dashboard Gestione Eventi</title>
        <defs>
          <linearGradient id="sbGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#E63329"/>
            <stop offset="50%"  stopColor="#9B2DB5"/>
            <stop offset="100%" stopColor="#1A56A0"/>
          </linearGradient>
          <linearGradient id="sbBarGrad" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0.95"/>
          </linearGradient>
        </defs>

        {/* Icona */}
        <rect x="4" y="4" width="60" height="60" rx="10" fill="url(#sbGrad)"/>

        {/* Ganci */}
        <rect x="18" y="0" width="5" height="10" rx="2.5" fill="#ffffff" opacity="0.9"/>
        <rect x="44" y="0" width="5" height="10" rx="2.5" fill="#ffffff" opacity="0.9"/>

        {/* Barre grafico */}
        <rect x="10" y="38" width="6" height="18" rx="2" fill="url(#sbBarGrad)"/>
        <rect x="20" y="30" width="6" height="26" rx="2" fill="url(#sbBarGrad)"/>
        <rect x="30" y="22" width="6" height="34" rx="2" fill="url(#sbBarGrad)"/>
        <rect x="40" y="28" width="6" height="28" rx="2" fill="url(#sbBarGrad)"/>
        <rect x="50" y="16" width="6" height="40" rx="2" fill="url(#sbBarGrad)"/>

        {/* Linea trend */}
        <polyline points="13,38 23,30 33,22 43,28 53,16"
          fill="none" stroke="#F59E0B" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="53" cy="16" r="2.5" fill="#F59E0B"/>

        {/* Testi — colonna a destra dell'icona */}
        <text x="74" y="22" fontFamily="sans-serif" fontSize="11" fontWeight="600" fill="#ffffff">Dashboard</text>
        <text x="74" y="36" fontFamily="sans-serif" fontSize="11" fontWeight="600" fill="#ffffff">Gestione</text>
        <text x="74" y="50" fontFamily="sans-serif" fontSize="11" fontWeight="600" fill="#ffffff">Eventi</text>

        {/* Linea separatrice */}
        <line x1="4" y1="74" x2="196" y2="74" stroke="rgba(255,255,255,0.2)" strokeWidth="1"/>

        {/* Sottotitolo centrato sotto tutto */}
        <text x="100" y="90" textAnchor="middle"
          fontFamily="sans-serif" fontSize="8.5" fontWeight="400"
          fill="rgba(255,255,255,0.55)" letterSpacing="1">
          Virtual Meeting Assistant
        </text>
      </svg>
    </Box>
  );
}

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const [clientiMap, setClientiMap] = useState({});

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "clienti"), (snap) => {
      const map = {};
      snap.docs.forEach((d) => { map[d.id] = d.data().nome; });
      setClientiMap(map);
    });
    return unsub;
  }, []);

  async function handleLogout() {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Errore logout:", error);
    }
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          backgroundColor: "#1A56A0",
          color: "white",
        },
      }}
    >
      <LogoVMA onClick={() => navigate("/")} />

      <Divider sx={{ borderColor: "rgba(255,255,255,0.2)" }} />

      <List sx={{ flex: 1, mt: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              onClick={() => navigate(item.path)}
              selected={location.pathname === item.path}
              sx={{
                mx: 1, borderRadius: 2, mb: 0.5,
                color: "white",
                "&.Mui-selected": {
                  backgroundColor: "rgba(255,255,255,0.2)",
                },
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.2)" }} />

      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Avatar src={currentUser?.photoURL} sx={{ width: 32, height: 32 }} />
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <Typography variant="caption" color="rgba(255,255,255,0.9)" noWrap display="block">
            {currentUser?.displayName}
          </Typography>
        </Box>
        <Notifiche
          clientiMap={clientiMap}
          aggiornaNotificheCallback={aggiornaNotifiche}
        />
        <Tooltip title="Esci">
          <LogoutIcon
            sx={{ color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 20 }}
            onClick={handleLogout}
          />
        </Tooltip>
      </Box>
    </Drawer>
  );
}